import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  DestroyRef,
  inject,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatChipOption } from '@angular/material/chips';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { CsvColumn } from 'src/app/models/csv-export.model';
import {
  DeviceDto,
  MaintenanceEventDto,
  PartDto,
} from 'src/app/models/device.model';
import { TitleCaseReplacePipe } from 'src/app/pipes/title-case-replace.pipe';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { DeviceService } from 'src/app/services/device.service';
import {
  getDeviceDisplayName,
  getDeviceUnitNumber,
} from 'src/app/utils/device-display';
import {
  extractUavPartSubType,
  extractUavPartType,
  normalizeDroneConfiguration,
} from 'src/app/utils/drone-configuration';
import { formatPartType } from 'src/app/utils/part-display';
import { buildTimestamp, formatTimestamp } from 'src/utils/general-utils';
import {
  MaintenanceCounter,
  PartId,
  PartSubType,
  PartType,
  PartWithMaintenanceDetails,
  ThresholdEntry,
  ThresholdType,
  TypeOfMaintenance,
  ValueType,
} from '../../../models/maintenancelogs.model';
import { ChipOption } from '../../chip-selector/chip-selector.component';
import {
  ColumnDefinition,
  ColumnFilterConfig,
} from '../../qs-table/column-definition.interface';

@Component({
  selector: 'app-drone-maintenance-logbook',
  // Stable hook for end-to-end tests, so specs address the section by test id rather than by
  // this component's element name.
  host: { 'data-testid': 'maintenance-logbook' },
  templateUrl: './drone-maintenance-logbook.component.html',
  styleUrl: './drone-maintenance-logbook.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class DroneMaintenanceLogbookComponent implements OnChanges {
  @ViewChild('allParts', { static: false, read: MatChipOption })
  allPartsChip!: MatChipOption;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @Input() device?: DeviceDto;
  maintenanceLogs: PartWithMaintenanceDetails[] = [];

  // Names each row after its maintenance event, so an end-to-end test addresses one entry's row
  // and cells by id instead of searching the table for its text.
  maintenanceRowTestId = (log: PartWithMaintenanceDetails): string => log.id;
  @Input() configurationType!: string;
  private deviceService = inject(DeviceService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private titleCaseReplacePipe = new TitleCaseReplacePipe();
  injector = inject(Injector);

  columns: ColumnDefinition<PartWithMaintenanceDetails>[] = [
    {
      type: 'data',
      field: 'partId.id',
      header: 'Part Id',
      format: (row) => row?.partId?.id ?? '',
      sortFn: (a, b, multiplier) => {
        return (
          (a.partId.id ?? '').localeCompare(b.partId.id ?? '') * multiplier
        );
      },
    },
    {
      type: 'data',
      field: 'partDisplay',
      header: this.translateService.instant('drone.maintenance_logbook.part'),
      format: (row) => this.formatPartType(row!),
      sortFn: (a, b, multiplier) => {
        return (
          a!.partId!.partType!.localeCompare(b!.partId!.partType!) * multiplier
        );
      },
      style: (row) => {
        return { 'font-weight': 'bold' };
      },
    },
    {
      type: 'data',
      field: 'typeOfMaintenance',
      header: this.translateService.instant('drone.maintenance_logbook.type'),
      format: (row) => {
        return this.titleCaseReplacePipe.transform(row!.typeOfMaintenance);
      },
    },
    {
      type: 'data',
      field: 'formattedCreatedAt',
      header: this.translateService.instant(
        'drone.maintenance_logbook.created_at',
      ),
    },
    {
      type: 'data',
      field: 'description',
      header: this.translateService.instant(
        'drone.maintenance_logbook.description',
      ),
    },
    {
      type: 'data',
      field: 'maintainer',
      header: this.translateService.instant(
        'drone.maintenance_logbook.maintainer',
      ),
    },
    //
  ];

  readonly MAINTENANCE_LOGS_CSV_COLUMNS: CsvColumn<PartWithMaintenanceDetails>[] =
    [
      {
        header: this.translateService.instant('drone.flight_his.drone_name'),
        accessor: () => this.deviceName,
      },
      {
        header: this.translateService.instant('drone.flight_his.drone_unit_no'),
        accessor: () => this.deviceUnitNumber,
      },

      {
        header: 'Part Id',
        accessor: 'partId.id',
        format: (v) => String(v ?? ''),
      },
      {
        header: this.translateService.instant('drone.maintenance_logbook.part'),
        accessor: 'partId.partType',
        format: (_v, row) => this.formatPartType(row),
      },
      {
        header: this.translateService.instant('drone.maintenance_logbook.type'),
        accessor: 'typeOfMaintenance',
        format: (_v, row) =>
          this.titleCaseReplacePipe.transform(row.typeOfMaintenance ?? ''),
      },
      {
        header: this.translateService.instant(
          'drone.maintenance_logbook.created_at',
        ),
        accessor: 'createdAt',
        format: (_v, row) => formatTimestamp(row.createdAt),
      },
      {
        header: this.translateService.instant(
          'drone.maintenance_logbook.description',
        ),
        accessor: 'description',
        format: (v) => String(v ?? ''),
      },
      {
        header: this.translateService.instant(
          'drone.maintenance_logbook.maintainer',
        ),
        accessor: 'maintainer',
        format: (v) => String(v ?? ''),
      },
      {
        header: this.translateService.instant(
          'drone.maintenance_logbook.comment',
        ),
        accessor: 'detailedDescription',
        format: (v) => String(v ?? ''),
      },
      {
        header: this.translateService.instant(
          'drone.maintenance_logbook.attachments',
        ),
        accessor: 'attachments',
        format: (_v, row) =>
          row.attachments?.length
            ? row.attachments
                .map((a) => a.split('/').pop()) // keep only file name
                .join(' | ')
            : '',
      },
    ];

  get deviceId(): string | undefined {
    return this.device?.deviceId?.trim() || undefined;
  }

  get deviceName(): string {
    return getDeviceDisplayName(this.device);
  }

  get deviceUnitNumber(): string {
    return getDeviceUnitNumber(this.device);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['device'] || changes['configurationType']) &&
      this.configurationType
    ) {
      this.loadMaintenanceLogs();
    }
  }

  private loadMaintenanceLogs() {
    if (!this.deviceId) {
      this.maintenanceLogs = [];
      return;
    }

    this.deviceService
      .getDeviceParts(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.maintenanceLogs = this.mapPartsToMaintenanceLogs(
            response.parts ?? [],
          );
        },
        error: (error) => {
          console.error('Error loading maintenance logs:', error);
          this.maintenanceLogs = [];
        },
      });
  }

  private mapPartsToMaintenanceLogs(
    parts: PartDto[],
  ): PartWithMaintenanceDetails[] {
    return parts
      .filter((part) => this.matchesSelectedConfiguration(part))
      .flatMap((part) =>
        (part.maintenanceEvents ?? []).map((event, index) =>
          this.mapMaintenanceEventToLog(part, event, index),
        ),
      )
      .sort(
        (a, b) => this.toTimestamp(b.createdAt) - this.toTimestamp(a.createdAt),
      );
  }

  private mapMaintenanceEventToLog(
    part: PartDto,
    event: MaintenanceEventDto,
    index: number,
  ): PartWithMaintenanceDetails {
    const partId = this.resolvePartId(part);
    const createdAt = event.eventTime ?? '';

    return {
      type: {
        partId,
        maintenanceLogs: [],
        maintenanceCounter: this.createEmptyCounter(createdAt),
        threshold: this.createEmptyThreshold(),
      },
      partId,
      qsUavPartType: extractUavPartType(part.details) || undefined,
      id: event.id ?? this.createMaintenanceEventRowId(partId, event, index),
      typeOfMaintenance:
        (event.typeOfMaintenance as TypeOfMaintenance | undefined) ??
        TypeOfMaintenance.UNKNOWN,
      createdAt,
      formattedCreatedAt: formatTimestamp(createdAt),
      description: event.description ?? '',
      maintainer: event.maintainer ?? '',
      detailedDescription: event.detailedDescription ?? '',
      detailedMaintainer: event.detailedMaintainer ?? event.maintainer ?? '',
      attachments: event.attachments ?? [],
    };
  }

  private matchesSelectedConfiguration(part: PartDto): boolean {
    if (this.configurationType === PartSubType.All) {
      return true;
    }

    return (
      normalizeDroneConfiguration(this.resolvePartConfiguration(part)) ===
      normalizeDroneConfiguration(this.configurationType)
    );
  }

  private resolvePartConfiguration(part: PartDto): string {
    const partSubTypeConfiguration = normalizeDroneConfiguration(
      extractUavPartSubType(part.details),
    );
    if (partSubTypeConfiguration) {
      return partSubTypeConfiguration;
    }

    const detailsConfiguration =
      part.details?.type === 'QsUavPart' ? part.details.configuration : '';

    return normalizeDroneConfiguration(detailsConfiguration);
  }

  /**
   * Part types the backend could not map end up as 'other'. For those we show the raw
   * CBDA part type in brackets, e.g. "Other (HW_TYPE_PARACHUTE)".
   */
  formatPartType(row: PartWithMaintenanceDetails): string {
    return formatPartType(row.partId?.partType, row.qsUavPartType);
  }

  private resolvePartId(part: PartDto): PartId {
    const detailsPartId =
      part.details?.type === 'QsUavPart' ? part.details.partId : undefined;
    const detailsPartType = extractUavPartType(part.details) || undefined;

    return {
      partType: this.resolvePartType(part.type ?? detailsPartType),
      partSubType: this.resolvePartSubType(extractUavPartSubType(part.details)),
      id: detailsPartId ?? part.partId ?? '',
    };
  }

  private resolvePartType(partType?: string): PartType {
    const normalizedPartType = partType?.toLowerCase() ?? '';

    switch (normalizedPartType) {
      case PartType.BODY:
        return PartType.BODY;
      case PartType.LEFT_WING:
        return PartType.LEFT_WING;
      case PartType.RIGHT_WING:
        return PartType.RIGHT_WING;
      case PartType.REAR:
        return PartType.REAR;
      case PartType.PAYLOAD:
        return PartType.PAYLOAD;
      case PartType.BATTERY:
        return PartType.BATTERY;
      case PartType.OTHER:
        return PartType.OTHER;
      default:
        break;
    }

    if (
      normalizedPartType.includes('fuselage') ||
      normalizedPartType.includes('body')
    ) {
      return PartType.BODY;
    }

    if (normalizedPartType.includes('left')) {
      return PartType.LEFT_WING;
    }

    if (normalizedPartType.includes('right')) {
      return PartType.RIGHT_WING;
    }

    if (
      normalizedPartType.includes('rear') ||
      normalizedPartType.includes('tail')
    ) {
      return PartType.REAR;
    }

    if (normalizedPartType.includes('payload')) {
      return PartType.PAYLOAD;
    }

    if (normalizedPartType.includes('battery')) {
      return PartType.BATTERY;
    }

    return PartType.OTHER;
  }

  private resolvePartSubType(rawPartSubType: string): PartSubType {
    const resolvedPartSubType = normalizeDroneConfiguration(rawPartSubType);
    if (resolvedPartSubType === PartSubType.VECOTORR24) {
      return PartSubType.VECOTORR24;
    }

    if (resolvedPartSubType === PartSubType.VECOTORR25) {
      return PartSubType.VECOTORR25;
    }

    if (resolvedPartSubType === PartSubType.SCORPIONR24) {
      return PartSubType.SCORPIONR24;
    }

    if (resolvedPartSubType === PartSubType.SCORPIONR25) {
      return PartSubType.SCORPIONR25;
    }

    return PartSubType.All;
  }

  private createEmptyCounter(updatedAt: string): MaintenanceCounter {
    return {
      type: ThresholdType.TOTAL_TIME,
      updatedAt,
      value: '0',
      threshold: '0',
      valueType: ValueType.SECONDS,
    };
  }

  private createEmptyThreshold(): ThresholdEntry {
    return {
      typeOfThreshold: ThresholdType.TOTAL_TIME,
      value: '0',
      valueType: ValueType.SECONDS,
    };
  }

  private createMaintenanceEventRowId(
    partId: PartId,
    event: MaintenanceEventDto,
    index: number,
  ): string {
    return [
      partId.partType,
      partId.partSubType,
      partId.id,
      event.eventTime ?? index,
    ].join('-');
  }

  private toTimestamp(timestamp?: string): number {
    if (!timestamp) {
      return 0;
    }

    const parsedTimestamp = Date.parse(timestamp);
    return Number.isFinite(parsedTimestamp) ? parsedTimestamp : 0;
  }

  //This should be moved to more generic place later
  generateFilterOptions(options: string[]): ChipOption[] {
    let chiOptions: ChipOption[] = [];
    options.forEach((option) => {
      chiOptions.push({
        displayName: this.translateService.instant(
          'drone.maintenance.part_type.' +
            option.toLowerCase().replace(/\s+/g, '_'),
        ),
        value: option,
        isSelected: true,
      });
    });
    return chiOptions;
  }

  columnFilterConfig: ColumnFilterConfig = {
    field: 'partDisplay',
    filterOptions: this.generateFilterOptions(Object.values(PartType)),
    filterFunc: (rows, selectedChips) => {
      return rows.filter((row) => selectedChips.includes(row.partId.partType));
    },
  };

  csvExportMaintenance(): void {
    const cols = this.MAINTENANCE_LOGS_CSV_COLUMNS;
    const csvService = this.injector.get(CsvExportService);

    csvService.exportToCsv(this.maintenanceLogs, cols, {
      fileName: `${this.deviceUnitNumber}_maintenance_logs_${this.configurationType}_${buildTimestamp()}.csv`,
    });
  }
}
